import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Check, Zap } from 'lucide-react';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import * as api from '@/services/api';

export default function ApiUsagePanel() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const [error, setError] = useState(null);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await api.getProvidersUsage();
    if (err) {
      setError(err);
    } else {
      setUsage(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const handleSwitch = async (providerName) => {
    setSwitching(providerName);
    const { error: err } = await api.switchProvider(providerName);
    if (err) {
      setError(err);
    } else {
      await fetchUsage();
    }
    setSwitching(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <p className="text-[#EF4444] font-mono text-sm">{error}</p>
        <button onClick={fetchUsage} className="button-primary mt-4 text-sm">Retry</button>
      </div>
    );
  }

  if (!usage) return null;

  return (
    <div data-testid="api-usage-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold font-syne text-[#E2E8F0]">Search Providers</h3>
        <button
          onClick={fetchUsage}
          className="flex items-center gap-1.5 text-xs font-mono text-[#94A3B8] hover:text-[#E2E8F0] transition-colors"
          data-testid="refresh-usage"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {Object.entries(usage).map(([name, info]) => (
          <ProviderCard
            key={name}
            name={name}
            info={info}
            switching={switching === name}
            onSwitch={() => handleSwitch(name)}
          />
        ))}
      </div>
    </div>
  );
}

function ProviderCard({ name, info, switching, onSwitch }) {
  const isActive = info.active;
  const isExhausted = info.searchesLeft === 0 && name === 'serpapi';
  const isLow = name === 'serpapi' && !isExhausted && info.searchesLeft != null && info.searchesLeft <= Math.round((info.searchesPerMonth || 0) * 0.1);

  const statusColor = isExhausted ? 'text-[#EF4444]'
    : (info.status === 'Unreachable' || info.status === 'Not configured') ? 'text-[#4A5568]'
    : isLow ? 'text-[#F59E0B]'
    : 'text-[#10B981]';

  const statusText = isExhausted ? 'Out of Searches'
    : info.status === 'Unreachable' ? 'Unreachable'
    : info.status === 'Not configured' ? 'Not Configured'
    : isLow ? 'Running Low'
    : info.status || 'Active';

  return (
    <div className={`card p-4 border ${isActive ? 'border-[#0070F3]' : 'border-[#1E2D45]'}`} data-testid={`provider-${name}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={16} className={isActive ? 'text-[#0070F3]' : 'text-[#4A5568]'} />
          <span className="text-sm font-syne font-bold text-[#E2E8F0]">{info.label}</span>
          {isActive && (
            <span className="flex items-center gap-1 text-[10px] font-mono bg-[#0070F3]/20 text-[#60A5FA] px-2 py-0.5 rounded-full">
              <Check size={10} /> Active
            </span>
          )}
        </div>
        {!isActive && (
          <button
            onClick={onSwitch}
            disabled={switching || info.status === 'Unreachable' || info.status === 'Not configured'}
            className="text-xs font-mono px-3 py-1.5 rounded-lg bg-[#1A2338] text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#0070F3]/20 border border-[#1E2D45] hover:border-[#0070F3] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`switch-to-${name}`}
          >
            {switching ? <LoadingSpinner size="sm" /> : 'Switch'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <p className="text-[10px] font-mono uppercase text-[#4A5568]">Status</p>
          <p className={`text-xs font-syne font-bold ${statusColor}`}>{statusText}</p>
        </div>

        <div>
          <p className="text-[10px] font-mono uppercase text-[#4A5568]">Plan</p>
          <p className="text-xs font-mono text-[#94A3B8]">{info.plan || '--'}</p>
        </div>

        {name === 'serpapi' && info.searchesLeft != null && (
          <div>
            <p className="text-[10px] font-mono uppercase text-[#4A5568]">Searches Left</p>
            <p className={`text-xs font-syne font-bold ${isExhausted ? 'text-[#EF4444]' : isLow ? 'text-[#F59E0B]' : 'text-[#E2E8F0]'}`}>
              {info.searchesLeft.toLocaleString()} / {(info.searchesPerMonth || 0).toLocaleString()}
            </p>
          </div>
        )}

        {name === 'dataforseo' && info.balance != null && (
          <div>
            <p className="text-[10px] font-mono uppercase text-[#4A5568]">Balance</p>
            <p className="text-xs font-syne font-bold text-[#E2E8F0]">
              {info.currency} {info.balance?.toFixed(2)}
            </p>
          </div>
        )}

        {name === 'searxng' && info.instance && (
          <div>
            <p className="text-[10px] font-mono uppercase text-[#4A5568]">Instance</p>
            <p className="text-xs font-mono text-[#94A3B8] truncate">{info.instance}</p>
          </div>
        )}
      </div>

      {name === 'serpapi' && info.searchesPerMonth > 0 && (
        <div className="mt-3">
          <div className="w-full h-2 bg-[#0A0F1E] rounded-full overflow-hidden border border-[#1E2D45]">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isExhausted ? 'bg-[#EF4444]' : isLow ? 'bg-[#F59E0B]' : 'bg-[#0070F3]'
              }`}
              style={{ width: `${Math.min(Math.round(((info.usedThisMonth || 0) / info.searchesPerMonth) * 100), 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
